import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Star, Quote } from "lucide-react";

/**
 * Testimonials + AggregateRating JSON-LD.
 * Pulled from /api/testimonials + /api/testimonials/aggregate on mount.
 * Injects Review + AggregateRating schemas so Google can surface stars.
 */
export const TestimonialsSection = () => {
  const [items, setItems] = useState([]);
  const [agg, setAgg] = useState(null);

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
    api.get("/testimonials/aggregate").then((r) => setAgg(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!items.length || !agg?.count) return;
    // Review + AggregateRating JSON-LD attached to the Organization "Axiony".
    const data = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Axiony",
      url: "https://axiony.co.in/",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: agg.average,
        reviewCount: agg.count,
        bestRating: 5,
        worstRating: 1,
      },
      review: items.slice(0, 8).map((t) => ({
        "@type": "Review",
        author: { "@type": "Person", name: t.author_name },
        reviewBody: t.quote,
        reviewRating: { "@type": "Rating", ratingValue: t.rating, bestRating: 5 },
        datePublished: t.published_at,
      })),
    };
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-testimonials", "true");
    s.textContent = JSON.stringify(data);
    document.head.appendChild(s);
    return () => s.remove();
  }, [items, agg]);

  if (!items.length) return null;

  return (
    <section
      className="max-w-6xl mx-auto px-6 py-24 border-t border-white/5"
      data-testid="testimonials-section"
    >
      <div className="mono-label">testimonials</div>
      <div className="mt-3 flex items-baseline gap-4 flex-wrap">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          What teams say about Axiony.
        </h2>
        {agg?.count && (
          <div className="flex items-center gap-1.5 text-sm text-slate-400" data-testid="testimonials-aggregate">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 ${
                    i < Math.round(agg.average) ? "fill-[#F59E0B] text-[#F59E0B]" : "text-slate-700"
                  }`}
                />
              ))}
            </div>
            <span className="font-mono text-slate-300">{agg.average}</span>
            <span className="text-slate-500">/ 5 · {agg.count} reviews</span>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
        {items.map((t, i) => (
          <blockquote
            key={t.testimonial_id}
            data-testid={`testimonial-${i}`}
            className="border border-white/10 bg-[#111827] rounded-xl p-6 flex flex-col"
          >
            <Quote className="w-5 h-5 text-[#06B6D4] mb-3 shrink-0" />
            <p className="text-slate-300 leading-relaxed flex-1">"{t.quote}"</p>
            <div className="mt-5 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-100 truncate">{t.author_name}</div>
                <div className="text-xs text-slate-500 truncate">
                  {t.author_role}
                  {t.author_role && t.author_company && " · "}
                  {t.author_company}
                </div>
              </div>
              <div className="flex shrink-0">
                {[...Array(5)].map((_, k) => (
                  <Star
                    key={k}
                    className={`w-3 h-3 ${
                      k < t.rating ? "fill-[#F59E0B] text-[#F59E0B]" : "text-slate-700"
                    }`}
                  />
                ))}
              </div>
            </div>
          </blockquote>
        ))}
      </div>
    </section>
  );
};
